import React, { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Lock, X, Loader2 } from 'lucide-react';
import salesService from '../services/salesService';

/**
 * FrozenStockDrawer - 展示销售订单某产品的预留（冻结）库存明细
 *
 * 功能：
 * - 右侧 Drawer 展示该产品的冻结明细（库位/批次/数量/来源客户/审核状态）
 * - 每行支持解冻（调用已有的 unfreeze-stock 接口）
 * - 解冻成功后通过 onUnfrozen 通知父组件刷新
 *
 * @param {boolean} open
 * @param {Function} onClose
 * @param {string} productCode
 * @param {string} productName
 * @param {Array} frozenLocations - executionProgress.per_product[x].frozen_locations
 * @param {number} pendingFreezeQty - 待审核冻结数量
 * @param {string} salesOrderId - 销售订单 _id（解冻接口用）
 * @param {Function} onUnfrozen - 解冻成功后的回调（刷新执行进度）
 */
const FrozenStockDrawer = ({
  open,
  onClose,
  productCode,
  productName,
  frozenLocations = [],
  pendingFreezeQty = 0,
  salesOrderId,
  onUnfrozen,
}) => {
  const [unfreezing, setUnfreezing] = useState(null); // location being unfrozen
  const [error, setError] = useState('');

  const totalQty = frozenLocations.reduce((sum, loc) => sum + (loc.quantity || 0), 0);

  const handleUnfreeze = async (loc) => {
    if (!salesOrderId || !productCode) return;
    const isPending = loc.freeze_status === 'pending';
    const confirmText = isPending
      ? `确定撤销库位「${loc.location}」的 ${loc.quantity} 件冻结申请吗？`
      : `确定解冻库位「${loc.location}」的 ${loc.quantity} 件库存吗？解冻后库存将恢复可用。`;
    if (!window.confirm(confirmText)) return;

    try {
      setUnfreezing(loc.location);
      setError('');
      await salesService.unfreezeStock(salesOrderId, {
        product_code: productCode,
        location: loc.location,
      });
      onUnfrozen?.();
    } catch (err) {
      setError(err.response?.data?.error || (isPending ? '撤销失败' : '解冻失败'));
    } finally {
      setUnfreezing(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[40vw] max-w-none p-0 overflow-hidden flex flex-col">
        <SheetHeader className="px-6 py-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-warning" />
            预留库存详情
          </SheetTitle>
          <SheetDescription>
            产品: <span className="font-medium text-foreground">{productName || productCode}</span>
            {productName && productCode !== productName && (
              <span className="text-muted-foreground ml-2">({productCode})</span>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-auto p-6">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription className="flex items-center justify-between">
                {error}
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                  <X className="h-4 w-4" />
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Summary */}
          <div className="flex gap-4 mb-4 text-sm">
            <div className="px-3 py-2 rounded bg-warning/10 border border-warning/20">
              共预留 <span className="font-bold text-warning">{totalQty}</span> 件
            </div>
            {pendingFreezeQty > 0 && (
              <div className="px-3 py-2 rounded bg-muted border">
                其中 <span className="font-bold">{pendingFreezeQty}</span> 件待仓库审核
              </div>
            )}
          </div>

          {frozenLocations.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Lock className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>暂无预留库存明细</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">库位</th>
                  <th className="px-3 py-2 text-left font-semibold">批次号</th>
                  <th className="px-3 py-2 text-right font-semibold">数量</th>
                  <th className="px-3 py-2 text-left font-semibold">来源客户</th>
                  <th className="px-3 py-2 text-left font-semibold">状态</th>
                  <th className="px-3 py-2 text-center font-semibold">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {frozenLocations.map((loc, index) => {
                  const isPending = loc.freeze_status === 'pending';
                  return (
                    <tr key={`${loc.movement_id || loc.location}-${index}`} className="hover:bg-muted/50">
                      <td className="px-3 py-2">{loc.location || 'main'}</td>
                      <td className="px-3 py-2">{loc.batch_number || '-'}</td>
                      <td className="px-3 py-2 text-right font-medium">{loc.quantity}</td>
                      <td className="px-3 py-2">{loc.source_client_name || '-'}</td>
                      <td className="px-3 py-2">
                        {isPending ? (
                          <Badge variant="outline" className="border-warning text-warning">待审核</Badge>
                        ) : (
                          <Badge variant="outline" className="border-success text-success">已冻结</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive border-destructive h-7"
                          disabled={unfreezing === loc.location}
                          onClick={() => handleUnfreeze(loc)}
                        >
                          {unfreezing === loc.location ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                          ) : null}
                          {isPending ? '撤销申请' : '解冻'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <p className="mt-4 text-xs text-muted-foreground">
            待审核的冻结申请尚未锁定库存，需仓库管理人员在「仓库管理 - 冻结审核」中批准后生效。
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FrozenStockDrawer;
